import { useState, type ChangeEventHandler, type MouseEventHandler } from 'react';
import { cn, useFieldContext } from '@bridebook/aura';
import { FakeInput } from '../fake-input';
import type { TimeInputProps } from './types';
import { formatTimeDisplay } from './utils';

/** TLDR: Native `<input type="time">` overlaid on a FakeInput — used on touch devices. */
export const TimeInputMobile = ({
  id,
  value,
  onChange,
  onBlur,
  disabled: disabledProp,
  error: errorProp,
  formatTime,
  is24Hour,
  showClearBtn = true,
  step: _step,
  ...fakeInputProps
}: TimeInputProps) => {
  const context = useFieldContext();
  const disabled = disabledProp ?? context?.disabled ?? false;
  const error = errorProp ?? context?.error ?? false;
  const [focused, setFocused] = useState(false);

  const displayValue = formatTime ? formatTime(value) : formatTimeDisplay(value, is24Hour);

  const handleNativeChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange(event.target.value);
    // Android doesn't fire blur when the native picker closes, so clear focus here.
    // On iOS the picker stays open after selection and blur fires on confirm — skip.
    if (/android/i.test(navigator.userAgent)) {
      setFocused(false);
    }
    onBlur?.();
  };

  const handleNativeClick: MouseEventHandler<HTMLInputElement> = (event) => {
    try {
      event.currentTarget.showPicker();
    } catch {
      // showPicker() can throw if already open or unsupported
    }
  };

  const handleNativeBlur = () => {
    setFocused(false);
    onBlur?.();
  };

  const handleClear = () => {
    onChange('');
    onBlur?.();
  };

  return (
    <div className="group relative">
      <FakeInput
        {...fakeInputProps}
        focused={focused}
        value={displayValue}
        error={error}
        disabled={disabled}
        showClearBtn={showClearBtn}
        onClear={handleClear}
        classNames={{
          ...fakeInputProps.classNames,
          clearButton: cn(fakeInputProps.classNames?.clearButton, 'z-20'),
        }}
      />

      <input
        id={id}
        type="time"
        value={value}
        onChange={handleNativeChange}
        onFocus={() => setFocused(true)}
        onBlur={handleNativeBlur}
        onClick={handleNativeClick}
        disabled={disabled}
        className="absolute inset-0 z-10 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
    </div>
  );
};
